import { useMemo } from 'react'
import '@splidejs/react-splide/css'
import { Splide, SplideSlide } from '@splidejs/react-splide'
import { GiftIcon } from '@/atoms/Icons/GiftIcon'
import { Image } from '@/atoms/Image'
import { Text } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils/IconTypes'
import { useTranslate } from '@/utils/translate/translate-client'
import { DavidButton } from './DavidButton'

const Pill = ({ children }: { children: React.ReactNode }) => {
  return (
    <Text
      weight="medium"
      className="photon-caption-md md:photon-caption-lg absolute bottom-4 left-1/2 flex h-10 shrink-0 -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full border-[3px] border-[#F9B74B] bg-white px-4 py-2 !text-[#411E09]"
    >
      <GiftIcon size={18} color={'#411E09' as IconColor} />
      {children}
    </Text>
  )
}

export function InspireGenSection() {
  const { t } = useTranslate('pif')

  const carouselImages = useMemo(() => {
    return [
      {
        src: '/v1760542668/theatrical/david/caleb-kylee-w-thank-you-01.webp',
        alt: 'Children with family',
        pill: <Pill>Gifted by Chris W. in Idaho</Pill>,
      },
      {
        src: '/v1760628285/theatrical/david/rachael-h-kid-thank-you-01.webp',
        alt: 'Children with family',
        pill: <Pill>Gifted by Rachael H. in Texas</Pill>,
      },
      {
        src: '/v1760628292/theatrical/david/cheyenne-kid-02-thank-you-img.webp',
        alt: 'Children with family',
        pill: <Pill>Gifted by Cheyenne O. in Utah</Pill>,
      },
      {
        src: '/v1760628480/theatrical/david/seth-kid-01-thank-you-img.webp',
        alt: 'Children holding thank you sign',
        pill: <Pill>Gifted by Seth T. in Utah</Pill>,
      },
    ]
  }, [])

  return (
    <section className="relative w-full px-4 py-[5.5rem] !pr-0 md:px-12 md:py-[7.5rem] lg:px-[4rem] lg:py-[10rem]">
      <div className="absolute inset-0 bg-[#faf0e7]" />
      <Image
        alt=""
        className="object-cover opacity-[0.55] mix-blend-color-burn"
        src="/v1760914561/theatrical/david/dirt-texture-on-white-01"
        fill
        sizes="100vw"
        loading="lazy"
      />

      <div className="relative mx-auto flex w-full shrink-0 flex-col content-stretch items-center gap-10 lg:flex-col-reverse lg:gap-[4rem]">
        {/* Carousel */}
        <div className="w-full">
          <Splide
            options={{
              type: 'loop',
              autoplay: true,
              pauseOnHover: false,
              resetProgress: false,
              interval: 3000,
              delay: 0,
              gap: '0.5rem',
              arrows: false,
              pagination: true,
              perPage: 3,
              perMove: 1,
              breakpoints: {
                [1024]: {
                  perPage: 2,
                  fixedWidth: '540px',
                  fixedHeight: '540px',
                  gap: '1rem',
                },
                [768]: {
                  perPage: 2,
                  fixedWidth: '333px',
                  fixedHeight: '333px',
                },
              },
              fixedWidth: '480px',
              fixedHeight: '480px',
            }}
            aria-label="Inspiring children images"
          >
            {carouselImages.map((image, index) => (
              <SplideSlide className="relative" key={index}>
                <Image
                  alt={image.alt}
                  className="h-[333px] w-[333px] rounded-3xl object-cover md:h-[540px] md:w-[540px] lg:h-[480px] lg:w-[480px]"
                  src={image.src}
                  width={540}
                  height={540}
                />
                {image.pill}
              </SplideSlide>
            ))}
          </Splide>
        </div>
        {/* Content */}
        <div className="relative w-full shrink-0 pr-4 md:pr-12 lg:pr-[4rem] lg:text-center">
          <div className="flex size-full flex-col justify-center">
            <div className="relative box-border flex w-full flex-col content-stretch items-start justify-center gap-8 p-0 md:gap-10">
              {/* Copy */}
              <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-3 not-italic lg:mx-auto lg:max-w-[680px]">
                <p className="font-angel-royal relative w-full text-5xl lowercase not-italic leading-[2.5rem] text-[#141447] md:text-[3.5rem] md:leading-[3rem]">
                  {t('davidInspireGenTitle', 'Inspire a Generation of Children')}
                </p>
                <Text weight="medium" className="photon-paragraph-md md:photon-paragraph-lg">
                  {t(
                    'davidInspireGenDescription',
                    'Help fill theaters with laughter, awe, and inspiration. When you Pay it Forward, you give kids the chance to experience DAVID—and discover a hero worth believing in.',
                  )}
                </Text>
              </div>

              {/* Buttons */}
              <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[8px] md:w-auto md:flex-row lg:mx-auto">
                <DavidButton variant="dark" openPifOverlay className="w-full md:w-auto">
                  {t('payItForward', 'Pay it Forward')}
                </DavidButton>
                <DavidButton variant="orange" href="/tickets/david" className="w-full md:w-auto">
                  {t('getShowtimes', 'Get Showtimes')}
                </DavidButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
